import * as React from "react";
import { useEffect } from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { REFINED } from "../risikoanalyseStep/tokens";

/**
 * Schlanke, nicht-blockierende Toast-Meldung im kvp-rd-Stil — ersetzt
 * `window.alert()` in der Vorgänge-Oberfläche. Portaliert nach `document.body`,
 * unten mittig, blendet sich nach `duration` selbst aus (Standard 4 s).
 * Kontrolliert: der Aufrufer hält den Toast-State und rendert die Komponente
 * mit `open`; `onClose` räumt den State ab.
 */
export interface ToastProps {
    open: boolean;
    message: string;
    tone?: "error" | "success" | "info";
    duration?: number;
    onClose: () => void;
}

export const Toast: React.FC<ToastProps> = ({ open, message, tone = "info", duration = 4000, onClose }) => {
    useEffect(() => {
        if (!open) return;
        const t = window.setTimeout(onClose, duration);
        return () => window.clearTimeout(t);
    }, [open, duration, onClose, message]);

    if (!open) return null;

    const palette =
        tone === "error"
            ? { bg: REFINED.errBg, fg: REFINED.err, line: REFINED.err }
            : tone === "success"
              ? { bg: REFINED.okBg, fg: REFINED.ok, line: REFINED.ok }
              : { bg: REFINED.aub50, fg: REFINED.aub, line: REFINED.aub };

    return createPortal(
        <div
            style={{
                position: "fixed",
                left: "50%",
                bottom: 28,
                transform: "translateX(-50%)",
                zIndex: 100003,
                maxWidth: "min(440px, calc(100vw - 32px))",
                fontFamily: REFINED.fontUI,
            }}
        >
            <div
                role="status"
                aria-live="polite"
                onClick={onClose}
                style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "11px 16px",
                    borderRadius: 12,
                    background: palette.bg,
                    color: palette.fg,
                    border: `1px solid ${palette.line}33`,
                    borderLeft: `4px solid ${palette.line}`,
                    boxShadow: REFINED.cardShadow,
                    fontSize: REFINED.fs.sm,
                    fontWeight: 600,
                    cursor: "pointer",
                }}
            >
                <span style={{ minWidth: 0 }}>{message}</span>
            </div>
        </div>,
        document.body,
    );
};

export default Toast;
